import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import cn from '@/utils/cn';
import type ILicense from '@/components/interfaces/License';
import compactSelectStyles from '../../shared/compactSelectStyles';
import SPACheckbox from '../../shared/SPACheckbox';
import SPAInput, { spaInputClass } from '../../shared/SPAInput';
import SPASelect from '../../shared/SPASelect';
import CopyrightYearsInput from './CopyrightYearsInput';
import { GRADE_OPTIONS, gradeLevelFor } from './gradeLevel';
import { Field, hintClass } from '../../shared/formPanel';
import type { ProgramFormValues } from './programForm';

interface LicenseOption {
  label: string;
  value: number;
}

const toLicenseOption = (license: ILicense): LicenseOption => ({ label: license.name, value: license.id });

interface ProgramInformationFieldsProps {
  form: UseFormReturn<ProgramFormValues>;
  licenses: ILicense[];
}

// The Program Information grid, shared by the edit section and the New Program page.
const ProgramInformationFields = ({ form, licenses }: ProgramInformationFieldsProps) => {
  const { control, formState: { errors }, register, watch } = form;
  const gradeLevel = gradeLevelFor(watch('grade'));
  const licenseOptions = useMemo(() => licenses.map(toLicenseOption), [licenses]);

  return (
    <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 md:tw-grid-cols-2">
      <Field required error={errors.code?.message} htmlFor="program_code" label="Code">
        <SPAInput id="program_code" {...register('code', { required: 'Code is required.' })} />
      </Field>
      <Field hint="Six hex characters, 0–9 and a–f." htmlFor="program_color" label="Color">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            aria-hidden="true"
            className={cn(
              'tw-h-[2.125rem] tw-w-[2.125rem] tw-shrink-0 tw-rounded',
              'tw-border tw-border-solid tw-border-spa-border-input',
            )}
            style={{ backgroundColor: `#${watch('color') || '522e91'}` }}
          />
          <SPAInput id="program_color" maxLength={6} {...register('color')} />
        </div>
      </Field>

      <Field required error={errors.title?.message} htmlFor="program_title" label="Title">
        <SPAInput id="program_title" {...register('title', { required: 'Title is required.' })} />
      </Field>
      <Field htmlFor="program_title_es" label="Spanish Title">
        <SPAInput id="program_title_es" {...register('title_es')} />
      </Field>

      <Field required error={errors.subtitle?.message} htmlFor="program_subtitle" label="Subtitle">
        <SPAInput id="program_subtitle" {...register('subtitle', { required: 'Subtitle is required.' })} />
      </Field>
      <Field htmlFor="program_subtitle_es" label="Spanish Subtitle">
        <SPAInput id="program_subtitle_es" {...register('subtitle_es')} />
      </Field>

      <Field required error={errors.edition_en?.message} htmlFor="program_edition_en" label="Edition (English)">
        <SPAInput id="program_edition_en" {...register('edition_en', { required: 'Edition is required.' })} />
      </Field>
      <Field htmlFor="program_edition_es" label="Edition (Spanish)">
        <SPAInput id="program_edition_es" {...register('edition_es')} />
      </Field>

      <Field hint="Exactly &copy; YYYY, using the HTML entity." htmlFor="program_copyright" label="Copyright">
        <SPAInput id="program_copyright" {...register('copyright')} />
      </Field>
      <Field htmlFor="program_additional_copyrights" label="Additional Copyrights">
        <Controller
          control={control}
          name="additional_copyrights"
          render={({ field }) => (
            <CopyrightYearsInput id="program_additional_copyrights" value={field.value} onChange={field.onChange} />
          )}
        />
      </Field>

      <Field
        required
        error={errors.homeschool_email?.message}
        htmlFor="program_homeschool_email"
        label="Homeschool Email"
      >
        <SPAInput
          id="program_homeschool_email"
          type="email"
          {...register('homeschool_email', { required: 'Homeschool email is required.' })}
        />
      </Field>
      <Field htmlFor="program_retirement_year" label="Retirement Year">
        <SPAInput
          id="program_retirement_year"
          inputMode="numeric"
          placeholder="YYYY"
          {...register('retirement_year')}
        />
      </Field>

      <Field className="md:tw-col-span-2" htmlFor="program_description_content" label="Description">
        <textarea
          className={cn(spaInputClass, 'tw-h-24 tw-resize-y tw-py-2')}
          id="program_description_content"
          {...register('description_content')}
        />
      </Field>

      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 md:tw-col-span-2">
        <Field required className="tw-w-28" error={errors.grade?.message} htmlFor="program_grade" label="Grade">
          <Controller
            control={control}
            name="grade"
            render={({ field }) => (
              <SPASelect
                inputId="program_grade"
                options={GRADE_OPTIONS}
                styles={compactSelectStyles}
                value={GRADE_OPTIONS.find(option => option.value === field.value) ?? null}
                onChange={option => field.onChange(option?.value ?? null)}
              />
            )}
            rules={{ validate: value => value !== null || 'Grade is required.' }}
          />
        </Field>
        {gradeLevel && (
          <span className="tw-pb-2 tw-text-sm tw-text-spa-secondary">Grade level: {gradeLevel}</span>
        )}
      </div>

      <Field
        required
        className="md:tw-col-span-2"
        error={errors.license_ids?.message}
        htmlFor="program_license_ids"
        label="Licenses"
      >
        <Controller
          control={control}
          name="license_ids"
          render={({ field }) => (
            <SPASelect<LicenseOption, true>
              isMulti
              inputId="program_license_ids"
              options={licenseOptions}
              placeholder="Select licenses"
              value={licenseOptions.filter(option => field.value.includes(option.value))}
              onChange={selected => field.onChange(selected.map(option => option.value))}
            />
          )}
          rules={{ validate: value => value.length > 0 || 'At least one license is required.' }}
        />
      </Field>

      <div className="md:tw-col-span-2">
        <SPACheckbox {...register('available_to_add')}>Available for all subscribers to add</SPACheckbox>
        <span className={cn(hintClass, 'tw-ml-6')}>
          Default states are set from the programs list, under Num of Licenses.
        </span>
      </div>
    </div>
  );
};

export default ProgramInformationFields;
